import type { ReactNode } from 'react'
import { Card } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import { formatNumeric } from '@/lib/utils/stringUtils'
import { OverviewCardFooter, OverviewCardTitle } from '@/components/shared/overview/OverviewBreakdownCard'
import { ShareMeter } from './AnalyticsReport'
import type { LLMReferralRow } from './llmReferrals'

export function LLMLogo({ logo, color, size = 16, className }: { logo: string | null; color: string; size?: number; className?: string }) {
  if (!logo) {
    return (
      <span
        className={cn('shrink-0 rounded-full', className)}
        style={{ backgroundColor: color, width: size, height: size }}
      />
    )
  }
  return (
    <img
      src={logo}
      alt=""
      className={cn('shrink-0 dark:invert', className)}
      style={{ width: size, height: size }}
    />
  )
}

const VALUE_COL = 'w-[60px] shrink-0 text-right'
const SHARE_COL = 'w-[110px] shrink-0'

/**
 * AI assistants card (Paper "App v3 · Analytics Sources"): referral visits per
 * assistant with a neutral share meter. Visits add up across assistants.
 */
export function AiAssistantsCard({
  rows,
  totalVisits,
  limit,
  headerAction,
  emptyMessage = 'No visits from AI assistants in this range yet.',
  footerLabel,
  onFooterClick,
  footer,
  className,
  alternateBody,
}: {
  rows: LLMReferralRow[]
  totalVisits: number
  limit?: number
  /** Replaces the total on the right of the header (e.g. a search field) */
  headerAction?: ReactNode
  emptyMessage?: string
  footerLabel?: string
  onFooterClick?: () => void
  /** Custom footer, e.g. pagination */
  footer?: ReactNode
  className?: string
  /** Replaces the column header and rows (an alternate chart view) */
  alternateBody?: ReactNode
}) {
  const shown = limit ? rows.slice(0, limit) : rows
  return (
    <Card className={cn('gap-0 py-0', className)}>
      <div className="flex min-h-[58px] items-center gap-2.5 px-[18px] py-3.5">
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <OverviewCardTitle>AI assistants</OverviewCardTitle>
          <p className="text-xs leading-4 text-muted-foreground">Visits referred by chat assistants</p>
        </div>
        {headerAction ?? (
          <span className="text-[13px] font-medium leading-4 text-foreground tabular-nums">
            {formatNumeric(totalVisits)}
          </span>
        )}
      </div>

      {alternateBody ? (
        <div className="border-t border-border/60 pt-3">{alternateBody}</div>
      ) : (
      <>
      <div className="flex h-8 shrink-0 items-center gap-3 border-y border-border/60 bg-muted/60 px-[18px] text-xs leading-4 text-muted-foreground">
        <span className="min-w-0 flex-1">Assistant</span>
        <span className={VALUE_COL}>Visits</span>
        <span className={cn(SHARE_COL, 'text-right')}>Share</span>
      </div>

      {shown.length === 0 ? (
        <div className="px-[18px] py-10 text-center text-[13px] text-muted-foreground">{emptyMessage}</div>
      ) : (
        <div className="flex flex-col">
          {shown.map((row) => (
            <div
              key={row.name}
              className="flex h-[52px] shrink-0 items-center gap-3 border-b border-border/60 px-[18px] last:border-0"
            >
              <span className="flex min-w-0 flex-1 items-center gap-2.5" title={row.domain}>
                <LLMLogo logo={row.logo} color={row.color} size={16} />
                <span className="truncate text-sm font-medium leading-[18px] text-foreground">{row.name}</span>
              </span>
              <span className={cn(VALUE_COL, 'text-[13px] font-medium leading-4 text-foreground tabular-nums')}>
                {formatNumeric(row.count)}
              </span>
              <span className={SHARE_COL}>
                <ShareMeter percent={row.percentage} />
              </span>
            </div>
          ))}
        </div>
      )}
      </>
      )}

      {footer}
      {!footer && footerLabel && <OverviewCardFooter label={footerLabel} onClick={onFooterClick} className="mt-auto" />}
    </Card>
  )
}
